import { reactive } from "vue";
import cart from "./cart";
import { formatPrice } from "./util";
import { isMobile } from "./util";
import { isGwpGiftVariantId, clampGwpQuantity } from "./gwp-config";

function clampGiftWithPurchaseLineQuantity(
  variantId: string,
  quantity: number
): number {
  if (!isGwpGiftVariantId(variantId)) {
    return quantity;
  }
  return clampGwpQuantity(quantity);
}

export const store = reactive({
  // App state (previously state.app)
  isMiniCartOpen: false,
  activeVariant: {},
  lastAdd: 0,
  cart: (window as any).BARREL.cart,

  // Additional state
  isBootstrapped: false,
  loading: false,

  // Computed getters
  get subtotal() {
    return formatPrice(this.cart.total_price);
  },
  get originalSubtotal() {
    return formatPrice(this.cart.original_total_price);
  },
  get isCartDiscounted() {
    if (!this.cart) {
      return false;
    }

    const original = this.cart.original_total_price;
    const total = this.cart.total_price;

    return (
      typeof original === "number" &&
      typeof total === "number" &&
      original > total
    );
  },
  get isMobile() {
    return isMobile();
  },

  // Mutations (converted to methods)
  bootstrap() {
    this.isBootstrapped = true;
  },
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  },
  refreshCart(data: any, options?: { skipGwp?: boolean }) {
    this.loading = false;

    if (data) {
      if (data.errors) {
        this.cart.errors = data.errors;
      } else {
        this.updateCart(data);
      }
    }

    if (!options?.skipGwp) {
      void import("./gift-with-purchase").then((m) =>
        m.scheduleGiftWithPurchaseSync()
      );
    }
  },
  toggleMiniCart() {
    if (!this.isMiniCartOpen) {
      this.getCart();
    }

    this.isMiniCartOpen = !this.isMiniCartOpen;
  },

  // Legacy update methods (keeping for backward compatibility)
  updateLastAdd(lastAdd: number) {
    this.lastAdd = lastAdd;
  },
  updateCart(cartData: any) {
    if (!cartData || typeof cartData !== "object") {
      this.cart = cartData;
      return;
    }
    const items = Array.isArray(cartData.items)
      ? cartData.items.map((line: any) =>
          line && typeof line === "object" ? { ...line } : line
        )
      : [];
    this.cart = { ...cartData, items };
  },
  updateActiveVariant(activeVariant: any) {
    this.activeVariant = activeVariant;
  },
  updateIsMiniCartOpen(isMiniCartOpen: boolean) {
    this.isMiniCartOpen = isMiniCartOpen;
  },

  // Actions (converted to async methods)
  async getCart() {
    this.setLoading(true);
    try {
      const data = await cart.get();
      this.refreshCart(data);
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  },

  async updateItemQuantity({
    quantity,
    id,
    variantId,
  }: {
    quantity: number;
    id: string;
    variantId?: string | number;
  }) {
    if (!id) {
      return;
    }

    const clampVariantId =
      variantId != null ? String(variantId) : String(id);
    const q = clampGiftWithPurchaseLineQuantity(clampVariantId, quantity);

    this.setLoading(true);
    try {
      const data = await cart.update({ quantity: q, id });
      this.refreshCart(data);
      return data;
    } catch (error) {
      this.setLoading(false);
      throw error;
    }
  },

  async addToCart(id: string, quantity: number) {
    const { items } = this.cart;

    // Adds the requested `quantity` to the current item.quantity
    const item = items.find((i: any) => i.variant_id === id);
    const totalQuantity = item ? item.quantity + quantity : quantity;

    return this.updateItemQuantity({ quantity: totalQuantity, id });
  },
});

export default store;
